/**
 * The trigger button and floating panel that `Popover` and `Menu` share. Not exported from the
 * package: use one of those two.
 *
 * The panel is a native `popover="auto"`, which gives it the top layer, light dismiss on an outside
 * click, Escape, and focus back on the trigger when it closes. `popoverTarget` on the button wires
 * the toggle with no handler of ours. `place` positions it and flips it upward near the bottom of
 * the viewport.
 */
import * as stylex from "@stylexjs/stylex";
import type { KeyboardEvent, ReactNode, RefObject } from "react";
import { useEffect, useId, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/layer.stylex";
import { layer } from "@lattice/tokens/layer.stylex";
import "@lattice/tokens/shadow.stylex";
import { shadow } from "@lattice/tokens/shadow.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { buttonStyle } from "./buttonStyle.ts";
import { focusRing } from "./focus.ts";
import { ChevronDownIcon } from "./icons.tsx";
import type { Align, Side } from "./position.ts";
import { followUntilClosed, place } from "./position.ts";
import { Tooltip } from "./Tooltip.tsx";
const GAP_PX = 4; // token-exempt: the measured gap between a trigger and its panel

const styles = {
  blockTrigger: {
    kzqmXN: "xh8yej3",
    kjj79g: "x1qughib",
    k8WAf4: "x1jyo4x1",
    k9WMMc: "x1yc453h",
    $$css: true
  },
  start: {
    k9WMMc: "x1yc453h",
    khDVqt: "xeaf4i8",
    kHjlTd: "xj0a0fe",
    kg5iWk: "xbrixnn",
    $$css: true
  },
  words: {
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  square: {
    kg3NbH: "xnjsko4",
    kmuXW: "x2lah0s",
    $$css: true
  },
  tight: {
    kzqmXN: "x150l1ig xyzrq1n",
    kAzted: "xn2nem0 xlivvtk",
    $$css: true
  }
};
export interface PopupProps {
  /** Names the panel, and an icon-only trigger. */
  readonly label: string;
  /** The words on the trigger. Without them the trigger shows `icon` alone and `label` names it. */
  readonly triggerContent?: string | undefined;
  /** Names the trigger by `label` as well as its words, when `label` contains them: "Account menu for ada@x.com". */
  readonly nameTrigger?: boolean | undefined;
  /** The trigger fills its container, with the words at the start and the chevron at the end. */
  readonly block?: boolean | undefined;
  readonly icon?: ReactNode;
  readonly role: "menu" | "dialog";
  readonly side?: Side | undefined;
  readonly align?: Align | undefined;
  readonly padded?: boolean;
  readonly testId?: string | undefined;
  /** Runs when the panel opens, with the panel, to move focus into it. */
  readonly onOpened?: (panel: HTMLElement) => void;
  readonly onPanelKeyDown?: (event: KeyboardEvent<HTMLElement>, close: () => void) => void;
  readonly onTriggerKeyDown?: (event: KeyboardEvent<HTMLButtonElement>, open: () => void) => void;
  readonly children: ReactNode;
}
interface Handles {
  readonly panel: RefObject<HTMLDivElement | null>;
  readonly trigger: RefObject<HTMLButtonElement | null>;
}

/** Words in a pill, or filling their container as a rounded row. An icon alone is a square. */
function triggerLook(words: boolean, block: boolean) {
  if (!words) return [buttonStyle.rounded, styles.square, styles.tight];
  return block ? [buttonStyle.rounded, buttonStyle.md, styles.blockTrigger] : [buttonStyle.pill, buttonStyle.md];
}
export function Popup({
  label,
  triggerContent,
  nameTrigger,
  block = false,
  icon,
  role,
  side = "bottom",
  align = "start",
  padded = false,
  testId,
  onOpened,
  onPanelKeyDown,
  onTriggerKeyDown,
  children
}: PopupProps) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const handles: Handles = {
    panel: useRef(null),
    trigger: useRef(null)
  };
  const wrap = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const panel = handles.panel.current;
    const anchor = wrap.current;
    if (!open || panel === null || anchor === null) return undefined;
    const settle = () => place(anchor, panel, side, align, GAP_PX);
    settle();
    onOpened?.(panel);
    return followUntilClosed(settle);
    // `onOpened` is a new function each render and must not reposition the panel as it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, align]);
  const close = () => {
    handles.panel.current?.hidePopover();
    handles.trigger.current?.focus();
  };
  const words = triggerContent !== undefined;
  const trigger = <button ref={handles.trigger} type="button" popoverTarget={panelId} aria-haspopup={role} aria-expanded={open} aria-controls={panelId} aria-label={words && nameTrigger !== true ? undefined : label} title={words && block ? triggerContent : undefined} data-testid={testId} onKeyDown={event => onTriggerKeyDown?.(event, () => handles.panel.current?.showPopover())} {...stylex.props(buttonStyle.base, triggerLook(words, block), buttonStyle[words ? "secondary" : "tertiary"], open && buttonStyle.pressed, focusRing)}>
      {words ? <>
          <span {...stylex.props(buttonStyle.label, styles.words, block && styles.start)}>
            {triggerContent}
          </span>
          <span className="x3nfvp2 x18pxznq">
            <ChevronDownIcon />
          </span>
        </> : <span {...stylex.props(buttonStyle.slot)}>{icon}</span>}
    </button>;
  return <span ref={wrap} {...{
    0: {
      className: "x3nfvp2 x193iq5w xeuugli"
    },
    1: {
      className: "x193iq5w xeuugli x78zum5 xh8yej3"
    }
  }[!!block << 0]}>
      {words ? trigger : <Tooltip id={`${panelId}-tip`} text={label}>
          {trigger}
        </Tooltip>}
      <div id={panelId} ref={handles.panel} popover="auto" role={role} aria-label={label} onToggle={event => setOpen(event.newState === "open")} onKeyDown={event => onPanelKeyDown?.(event, close)} {...{
      0: {
        className: "xixxii4 x1anvkwx x1ghz6dp x12kinc7 xl3862 xowozqq x1odjw0f x1xqhfp xdq7b4a x1y0btm7 xv9mh3h x1fbwzx x40k5rp xm8xz4y x4n3pnu x10yvtpf"
      },
      1: {
        className: "xixxii4 x1anvkwx x1ghz6dp x12kinc7 xl3862 xowozqq x1odjw0f xdq7b4a x1y0btm7 xv9mh3h x1fbwzx x40k5rp xm8xz4y x4n3pnu x10yvtpf x1a91ssu"
      }
    }[!!padded << 0]}>
        {open && children}
      </div>
    </span>;
}